import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PartnerRoute } from '@/components/PartnerRoute';
import { LoginPage } from '@/pages/LoginPage';
import { ImpersonateConsumer } from '@/pages/ImpersonateConsumer';
import { DashboardPage } from '@/pages/DashboardPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { NAV_ITEMS } from '@/config/nav';

const MembersPage = lazy(() =>
  import('@/pages/members/MembersPage').then((m) => ({ default: m.MembersPage })),
);
const MemberGroupsPage = lazy(() =>
  import('@/pages/members/MemberGroupsPage').then((m) => ({ default: m.MemberGroupsPage })),
);
const MemberBlacklistPage = lazy(() =>
  import('@/pages/members/MemberBlacklistPage').then((m) => ({ default: m.MemberBlacklistPage })),
);
const TicketsPage = lazy(() =>
  import('@/pages/tickets/TicketsPage').then((m) => ({ default: m.TicketsPage })),
);
const NewTicketPage = lazy(() =>
  import('@/pages/tickets/NewTicketPage').then((m) => ({ default: m.NewTicketPage })),
);
const TicketDetailPage = lazy(() =>
  import('@/pages/tickets/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })),
);
const WalletPage = lazy(() =>
  import('@/pages/wallet/WalletPage').then((m) => ({ default: m.WalletPage })),
);
const FinancePage = lazy(() =>
  import('@/pages/finance/FinancePage').then((m) => ({ default: m.FinancePage })),
);
const GamesPage = lazy(() =>
  import('@/pages/games/GamesPage').then((m) => ({ default: m.GamesPage })),
);
const ArtApprovalsPage = lazy(() =>
  import('@/pages/art/ArtApprovalsPage').then((m) => ({ default: m.ArtApprovalsPage })),
);
const ArtGamesPage = lazy(() =>
  import('@/pages/art/ArtGamesPage').then((m) => ({ default: m.ArtGamesPage })),
);
const ArtGamePage = lazy(() =>
  import('@/pages/art/ArtGamePage').then((m) => ({ default: m.ArtGamePage })),
);
const ArtBatchesPage = lazy(() =>
  import('@/pages/art/ArtBatchesPage').then((m) => ({ default: m.ArtBatchesPage })),
);
const ArtBatchNewPage = lazy(() =>
  import('@/pages/art/ArtBatchNewPage').then((m) => ({ default: m.ArtBatchNewPage })),
);
const ArtBatchPage = lazy(() =>
  import('@/pages/art/ArtBatchPage').then((m) => ({ default: m.ArtBatchPage })),
);
const ArtSettingsPage = lazy(() =>
  import('@/pages/art/ArtSettingsPage').then((m) => ({ default: m.ArtSettingsPage })),
);
const ArtCostsPage = lazy(() =>
  import('@/pages/art/ArtCostsPage').then((m) => ({ default: m.ArtCostsPage })),
);
const ArtKitPage = lazy(() =>
  import('@/pages/art/ArtKitPage').then((m) => ({ default: m.ArtKitPage })),
);
const ArtThemesPage = lazy(() =>
  import('@/pages/art/ArtThemesPage').then((m) => ({ default: m.ArtThemesPage })),
);
const ArtThemePage = lazy(() =>
  import('@/pages/art/ArtThemePage').then((m) => ({ default: m.ArtThemePage })),
);
const ArtThemeNewPage = lazy(() =>
  import('@/pages/art/ArtThemePage').then((m) => ({ default: m.ArtThemeNewPage })),
);
const BettingPage = lazy(() =>
  import('@/pages/betting/BettingPage').then((m) => ({ default: m.BettingPage })),
);
const AdminsPage = lazy(() =>
  import('@/pages/users/AdminsPage').then((m) => ({ default: m.AdminsPage })),
);
const RolesPage = lazy(() =>
  import('@/pages/roles/RolesPage').then((m) => ({ default: m.RolesPage })),
);
const PromotionPage = lazy(() =>
  import('@/pages/promotion/PromotionPage').then((m) => ({ default: m.PromotionPage })),
);
const BonusPage = lazy(() =>
  import('@/pages/bonus/BonusPage').then((m) => ({ default: m.BonusPage })),
);
const AffiliateLayout = lazy(() =>
  import('@/pages/affiliate/AffiliateLayout').then((m) => ({ default: m.AffiliateLayout })),
);
const PartnersPage = lazy(() =>
  import('@/pages/affiliate/PartnersPage').then((m) => ({ default: m.PartnersPage })),
);
const GroupsPage = lazy(() =>
  import('@/pages/affiliate/GroupsPage').then((m) => ({ default: m.GroupsPage })),
);
const GroupRevenuePage = lazy(() =>
  import('@/pages/affiliate/GroupRevenuePage').then((m) => ({ default: m.GroupRevenuePage })),
);
const AffiliateSettingsPage = lazy(() =>
  import('@/pages/affiliate/AffiliateSettingsPage').then((m) => ({
    default: m.AffiliateSettingsPage,
  })),
);
const PaymentsPage = lazy(() =>
  import('@/pages/affiliate/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const PaymentSummaryPage = lazy(() =>
  import('@/pages/affiliate/PaymentSummaryPage').then((m) => ({ default: m.PaymentSummaryPage })),
);
const RevenueReportPage = lazy(() =>
  import('@/pages/affiliate/RevenueReportPage').then((m) => ({ default: m.RevenueReportPage })),
);
const MonthlyReportPage = lazy(() =>
  import('@/pages/affiliate/MonthlyReportPage').then((m) => ({ default: m.MonthlyReportPage })),
);
const RiskPage = lazy(() =>
  import('@/pages/risk/RiskPage').then((m) => ({ default: m.RiskPage })),
);
const ReportsLayout = lazy(() =>
  import('@/pages/reports/ReportsLayout').then((m) => ({ default: m.ReportsLayout })),
);
const ReportsOverviewPage = lazy(() =>
  import('@/pages/reports/OverviewPage').then((m) => ({ default: m.OverviewPage })),
);
const WinlossByLinePage = lazy(() =>
  import('@/pages/reports/WinlossByLinePage').then((m) => ({ default: m.WinlossByLinePage })),
);
const WinlossDailyPage = lazy(() =>
  import('@/pages/reports/WinlossDailyPage').then((m) => ({ default: m.WinlossDailyPage })),
);
const WinlossProvidersPage = lazy(() =>
  import('@/pages/reports/WinlossProvidersPage').then((m) => ({ default: m.WinlossProvidersPage })),
);
const FinanceDailyPage = lazy(() =>
  import('@/pages/reports/FinanceDailyPage').then((m) => ({ default: m.FinanceDailyPage })),
);
const FinanceSummaryPage = lazy(() =>
  import('@/pages/reports/FinanceSummaryPage').then((m) => ({ default: m.FinanceSummaryPage })),
);
const FinanceChannelsPage = lazy(() =>
  import('@/pages/reports/FinanceChannelsPage').then((m) => ({ default: m.FinanceChannelsPage })),
);
const FinanceBanksPage = lazy(() =>
  import('@/pages/reports/FinanceBanksPage').then((m) => ({ default: m.FinanceBanksPage })),
);
const MemberStatsPage = lazy(() =>
  import('@/pages/reports/MemberStatsPage').then((m) => ({ default: m.MemberStatsPage })),
);
const FirstDepositSummaryPage = lazy(() =>
  import('@/pages/reports/FirstDepositSummaryPage').then((m) => ({
    default: m.FirstDepositSummaryPage,
  })),
);
const FirstDepositsPage = lazy(() =>
  import('@/pages/reports/FirstDepositsPage').then((m) => ({ default: m.FirstDepositsPage })),
);
const ExportsPage = lazy(() =>
  import('@/pages/reports/ExportsPage').then((m) => ({ default: m.ExportsPage })),
);
const ContentPage = lazy(() =>
  import('@/pages/content/ContentPage').then((m) => ({ default: m.ContentPage })),
);
const NotificationPage = lazy(() =>
  import('@/pages/notification/NotificationPage').then((m) => ({ default: m.NotificationPage })),
);
const AuditPage = lazy(() =>
  import('@/pages/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const AuthSessionsPage = lazy(() =>
  import('@/pages/auth-sessions/AuthSessionsPage').then((m) => ({ default: m.AuthSessionsPage })),
);
const AccountPage = lazy(() =>
  import('@/pages/account/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const TenancyPage = lazy(() =>
  import('@/pages/tenancy/TenancyPage').then((m) => ({ default: m.TenancyPage })),
);
const SettlementsPage = lazy(() =>
  import('@/pages/settlements/SettlementsPage').then((m) => ({ default: m.SettlementsPage })),
);
const ReconcilePage = lazy(() =>
  import('@/pages/reconcile/ReconcilePage').then((m) => ({ default: m.ReconcilePage })),
);
const MySitePage = lazy(() =>
  import('@/pages/site/MySitePage').then((m) => ({ default: m.MySitePage })),
);
const ToolsPage = lazy(() =>
  import('@/pages/tools/ToolsPage').then((m) => ({ default: m.ToolsPage })),
);

const BUILT = new Set([
  '/members',
  '/tickets',
  '/wallet',
  '/finance',
  '/games',
  '/art/approvals',
  '/art/games',
  '/art/settings',
  '/art/costs',
  '/art/kit',
  '/art/themes',
  '/art/batches',
  '/betting',
  '/users',
  '/roles',
  '/promotion',
  '/bonus',
  '/affiliate',
  '/risk',
  '/reports',
  '/content',
  '/notification',
  '/audit',
  '/settings',
  '/auth-sessions',
  '/account',
  '/organizations',
  '/settlements',
  '/reconcile',
  '/site',
  '/tools',
]);

const PartnerLayout = lazy(() =>
  import('@/pages/partner-portal/PartnerLayout').then((m) => ({ default: m.PartnerLayout })),
);
const PartnerHomePage = lazy(() =>
  import('@/pages/partner-portal/PartnerHomePage').then((m) => ({ default: m.PartnerHomePage })),
);
const PartnerDashboardPage = lazy(() =>
  import('@/pages/partner-portal/PartnerDashboardPage').then((m) => ({ default: m.PartnerDashboardPage })),
);
const PartnerSignupPage = lazy(() =>
  import('@/pages/partner-portal/PartnerSignupPage').then((m) => ({ default: m.PartnerSignupPage })),
);
const PartnerLinksPage = lazy(() =>
  import('@/pages/partner-portal/PartnerLinksPage').then((m) => ({ default: m.PartnerLinksPage })),
);
const PartnerMembersPage = lazy(() =>
  import('@/pages/partner-portal/PartnerMembersPage').then((m) => ({ default: m.PartnerMembersPage })),
);
const PartnerDownlinePage = lazy(() =>
  import('@/pages/partner-portal/PartnerDownlinePage').then((m) => ({ default: m.PartnerDownlinePage })),
);
const PartnerActivityReportPage = lazy(() =>
  import('@/pages/partner-portal/PartnerActivityReportPage').then((m) => ({
    default: m.PartnerActivityReportPage,
  })),
);
const PartnerPeriodsReportPage = lazy(() =>
  import('@/pages/partner-portal/PartnerPeriodsReportPage').then((m) => ({
    default: m.PartnerPeriodsReportPage,
  })),
);
const PartnerMemberWinlossPage = lazy(() =>
  import('@/pages/partner-portal/PartnerMemberWinlossPage').then((m) => ({
    default: m.PartnerMemberWinlossPage,
  })),
);
const PartnerProviderWinlossPage = lazy(() =>
  import('@/pages/partner-portal/PartnerProviderWinlossPage').then((m) => ({
    default: m.PartnerProviderWinlossPage,
  })),
);
const PartnerCashReportPage = lazy(() =>
  import('@/pages/partner-portal/PartnerCashReportPage').then((m) => ({ default: m.PartnerCashReportPage })),
);
const PartnerContactPage = lazy(() =>
  import('@/pages/partner-portal/PartnerContactPage').then((m) => ({ default: m.PartnerContactPage })),
);

function RouteFallback() {
  return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/impersonate" element={<ImpersonateConsumer />} />
      <Route path="/partner/login" element={<LoginPage subjectKind="partner" />} />
      <Route
        path="/partner/signup"
        element={
          <Suspense fallback={<RouteFallback />}>
            <PartnerSignupPage />
          </Suspense>
        }
      />
      <Route
        path="/partner"
        element={
          <PartnerRoute>
            <Suspense fallback={<RouteFallback />}>
              <PartnerLayout />
            </Suspense>
          </PartnerRoute>
        }
      >
        <Route index element={<PartnerHomePage />} />
        <Route path="dashboard" element={<PartnerDashboardPage />} />
        <Route path="links" element={<PartnerLinksPage />} />
        <Route path="members" element={<PartnerMembersPage />} />
        <Route path="downline" element={<PartnerDownlinePage />} />
        <Route path="reports/activity" element={<PartnerActivityReportPage />} />
        <Route path="reports/periods" element={<PartnerPeriodsReportPage />} />
        <Route path="reports/member-winloss" element={<PartnerMemberWinlossPage />} />
        <Route path="reports/provider-winloss" element={<PartnerProviderWinlossPage />} />
        <Route path="reports/cash" element={<PartnerCashReportPage />} />
        <Route path="contact" element={<PartnerContactPage />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />

        {/* Phase 16 core modules */}
        <Route
          path="members"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MembersPage />
            </Suspense>
          }
        />
        <Route
          path="members/groups"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MemberGroupsPage />
            </Suspense>
          }
        />
        <Route
          path="members/blacklist"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MemberBlacklistPage />
            </Suspense>
          }
        />
        <Route
          path="tickets"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TicketsPage />
            </Suspense>
          }
        />
        <Route
          path="tickets/new"
          element={
            <Suspense fallback={<RouteFallback />}>
              <NewTicketPage />
            </Suspense>
          }
        />
        {/* After `tickets/new` for readability only — react-router v6 ranks a
            static segment above a dynamic one whatever the declaration order. */}
        <Route
          path="tickets/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TicketDetailPage />
            </Suspense>
          }
        />
        <Route
          path="wallet"
          element={
            <Suspense fallback={<RouteFallback />}>
              <WalletPage />
            </Suspense>
          }
        />
        <Route
          path="finance"
          element={
            <Suspense fallback={<RouteFallback />}>
              <FinancePage />
            </Suspense>
          }
        />
        <Route
          path="games"
          element={
            <Suspense fallback={<RouteFallback />}>
              <GamesPage />
            </Suspense>
          }
        />
        <Route
          path="art/approvals"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtApprovalsPage />
            </Suspense>
          }
        />
        <Route
          path="art/games"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtGamesPage />
            </Suspense>
          }
        />
        <Route
          path="art/settings"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="art/games/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtGamePage />
            </Suspense>
          }
        />
        <Route
          path="art/costs"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtCostsPage />
            </Suspense>
          }
        />
        <Route
          path="art/kit"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtKitPage />
            </Suspense>
          }
        />
        <Route
          path="art/themes"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtThemesPage />
            </Suspense>
          }
        />
        <Route
          path="art/themes/new"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtThemeNewPage />
            </Suspense>
          }
        />
        <Route
          path="art/themes/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtThemePage />
            </Suspense>
          }
        />
        <Route
          path="art/batches"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtBatchesPage />
            </Suspense>
          }
        />
        <Route
          path="art/batches/new"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtBatchNewPage />
            </Suspense>
          }
        />
        <Route
          path="art/batches/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArtBatchPage />
            </Suspense>
          }
        />
        <Route
          path="betting"
          element={
            <Suspense fallback={<RouteFallback />}>
              <BettingPage />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AdminsPage />
            </Suspense>
          }
        />
        <Route
          path="roles"
          element={
            <Suspense fallback={<RouteFallback />}>
              <RolesPage />
            </Suspense>
          }
        />

        {/* Phase 17 modules */}
        <Route
          path="promotion"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PromotionPage />
            </Suspense>
          }
        />
        <Route
          path="bonus"
          element={
            <Suspense fallback={<RouteFallback />}>
              <BonusPage />
            </Suspense>
          }
        />
        {/* Affiliate: one route per view, sub-nav from `AFFILIATE_NAV`
            (`affiliate-nav.test.ts` keeps the two in step). */}
        <Route
          path="affiliate"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AffiliateLayout />
            </Suspense>
          }
        >
          <Route index element={<PartnersPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="groups/:groupId/revenue" element={<GroupRevenuePage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/summary" element={<PaymentSummaryPage />} />
          <Route path="reports/revenue" element={<RevenueReportPage />} />
          <Route path="reports/monthly" element={<MonthlyReportPage />} />
          <Route path="settings" element={<AffiliateSettingsPage />} />
          <Route path="settlements" element={<Navigate to="/affiliate/payments" replace />} />
          <Route path="payouts" element={<Navigate to="/affiliate/payments" replace />} />
          <Route path="reports" element={<Navigate to="/affiliate/reports/revenue" replace />} />
        </Route>
        <Route
          path="risk"
          element={
            <Suspense fallback={<RouteFallback />}>
              <RiskPage />
            </Suspense>
          }
        />
        {/* Reports: one route per view. The layout owns the sub-nav and the
            per-page explanation sheet; `REPORTS_NAV` is the list it renders and
            `reports-nav.test.ts` asserts the two stay in step. */}
        <Route
          path="reports"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportsLayout />
            </Suspense>
          }
        >
          <Route index element={<ReportsOverviewPage />} />
          <Route path="winloss" element={<WinlossByLinePage />} />
          <Route path="winloss/daily" element={<WinlossDailyPage />} />
          <Route path="winloss/providers" element={<WinlossProvidersPage />} />
          <Route path="finance" element={<FinanceDailyPage />} />
          <Route path="finance/summary" element={<FinanceSummaryPage />} />
          <Route path="finance/channels" element={<FinanceChannelsPage />} />
          <Route path="finance/banks" element={<FinanceBanksPage />} />
          <Route path="members" element={<MemberStatsPage />} />
          <Route path="first-deposits" element={<FirstDepositSummaryPage />} />
          <Route path="first-deposits/list" element={<FirstDepositsPage />} />
          <Route path="exports" element={<ExportsPage />} />
        </Route>
        <Route
          path="content"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ContentPage />
            </Suspense>
          }
        />
        <Route
          path="notification"
          element={
            <Suspense fallback={<RouteFallback />}>
              <NotificationPage />
            </Suspense>
          }
        />
        <Route
          path="audit"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AuditPage />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="auth-sessions"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AuthSessionsPage />
            </Suspense>
          }
        />

        {/* Phase 21: profile / account */}
        <Route
          path="account"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AccountPage />
            </Suspense>
          }
        />

        {/* P4: organization hierarchy + frontend catalog (svc-tenancy backend) */}
        <Route
          path="organizations"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TenancyPage />
            </Suspense>
          }
        />

        {/* P5: MLM node settlements + credit/explainer */}
        <Route
          path="settlements"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SettlementsPage />
            </Suspense>
          }
        />

        {/* Money-path reconciliation runs (cron-persisted, reconcile:read) */}
        <Route
          path="reconcile"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReconcilePage />
            </Suspense>
          }
        />

        {/* Agent self-service white-label config (site:manage) */}
        <Route
          path="site"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MySitePage />
            </Suspense>
          }
        />

        {/* Operator tooling. Registered unconditionally; the page itself renders
            the 404 unless dev tools are enabled for this bundle and the
            operator is root. */}
        <Route
          path="tools"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ToolsPage />
            </Suspense>
          }
        />

        {/* Safety net: any nav item not yet wired falls through to a placeholder. */}
        {NAV_ITEMS.filter((i) => i.path !== '/' && !BUILT.has(i.path)).map((item) => (
          <Route
            key={item.path}
            path={item.path.replace(/^\//, '')}
            element={<PlaceholderPage titleKey={item.labelKey} slug={item.path.slice(1)} />}
          />
        ))}
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
